import {
  faArrowUpRightFromSquare,
  faCheck,
  faPlus,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Button,
  Grid,
  Icon,
  Input,
  Message,
  RadioCard,
  Tooltip,
} from "@intility/bifrost-react";
import Drawer from "@intility/bifrost-react/Drawer";
import Select from "@intility/bifrost-react-select";
import { useEffect, useState } from "react";
import type { CreateResourceTypeRequest, ResourceType } from "~/api/fga-types";
import { slugify } from "~/lib/clientTypes";
import {
  composeDynamicRegex,
  deriveSegmentTemplate,
  ID_PATTERNS,
  isValidPlaceholder,
} from "~/lib/resourceType";
import ArchetypeShowcase from "./ArchetypeShowcase";
import type { BlueprintSelection } from "./BlueprintCanvas";

interface LearnStep {
  title: string;
}

export const LEARN_STEPS: LearnStep[] = [
  { title: "Planning your structure" },
  { title: "Resource types" },
  { title: "Create a resource type" },
  { title: "Add your first resource" },
  { title: "Done!" },
];

interface BlueprintLearnProps {
  step: number;
  onCreateType?: (
    req: CreateResourceTypeRequest,
  ) => Promise<ResourceType | undefined>;
  busy?: boolean;
  createdTypes?: ResourceType[];
}

export default function BlueprintLearn({
  step,
  onCreateType,
  busy,
  createdTypes,
}: BlueprintLearnProps) {
  const current = LEARN_STEPS[step];

  return (
    <>
      <h2 className="bf-h2" style={{ margin: "0 0 8px" }}>
        {current.title}
      </h2>
      {step === 0 && <PlanningStructureContent />}
      {step === 1 && <ResourceTypesContent />}
      {step === 2 && (
        <CreateResourceTypeContent
          onCreateType={onCreateType}
          busy={busy}
          createdTypes={createdTypes ?? []}
        />
      )}
      {step === 3 && <AddFirstResourceContent />}
      {step === 4 && <DoneContent />}
    </>
  );
}

function PlanningStructureContent() {
  return (
    <>
      <p className="bf-p" style={{ margin: "0 0 24px" }}>
        Let's plan how your platform should structure its authorization. Look at
        the three examples below to see what structures following good practice
        look like. These show the simplest form of each pattern, but you can
        combine them within a single platform to match your needs.
      </p>
      <ArchetypeShowcase />
      <h3 className="bf-h3" style={{ margin: "24px 0 8px" }}>
        Resource paths
      </h3>
      <p className="bf-p" style={{ margin: "0 0 8px" }}>
        Each resource (one of the boxes above) is identified by a path, exactly
        like folders on a computer. Every path starts with the company and
        platform the resource belongs to. You don't interact with these paths
        directly, but knowing how they look helps you make good choices when
        building your blueprint. Here's how a path might look for the three
        examples above:
      </p>
      <ul className="bf-p" style={{ margin: 0 }}>
        <li>
          <strong>Flat</strong>:{" "}
          <code>resource:nimpos/operations-manager/alerts</code>
        </li>
        <li>
          <strong>Collection</strong>:{" "}
          <code>resource:nimpos/workload-manager/servers/srv-123</code>
        </li>
        <li>
          <strong>Nested</strong>:{" "}
          <code>
            resource:nimpos/identity-manager/regions/reg-oslo/subscriptions/sub-oslokontoret/users
          </code>
        </li>
      </ul>
    </>
  );
}

function ResourceTypesContent() {
  return (
    <>
      <p className="bf-p" style={{ margin: "0 0 12px" }}>
        A resource type is a kind of thing you control access to, like a server,
        a project, or a dashboard. You first define the types you need, then
        place them into your platform's structure in the following steps.
      </p>
      <p className="bf-p" style={{ margin: "0 0 12px" }}>
        Types are shared across platforms through a catalog, so if two platforms
        use the same concept they're easy to find. Access still stays separate:
        a <code>server</code> under one platform is not the same access as a{" "}
        <code>server</code> under another. Shared naming just makes them
        discoverable. Go to the next page to create your first type.
      </p>
      <a
        href="/blueprint-resources"
        className="bf-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        Browse the resource catalog
        <Icon icon={faArrowUpRightFromSquare} marginLeft />
      </a>
    </>
  );
}

function CreateResourceTypeContent({
  onCreateType,
  busy,
  createdTypes,
}: {
  onCreateType?: (
    req: CreateResourceTypeRequest,
  ) => Promise<ResourceType | undefined>;
  busy?: boolean;
  createdTypes: ResourceType[];
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      <p className="bf-p" style={{ margin: "0 0 12px" }}>
        Every resource type is either <strong>static</strong>, meaning there is
        exactly one of it (like <code>billing</code> or <code>audit-log</code>
        ), or <strong>dynamic</strong>, meaning there can be many instances
        (like <code>server</code> or <code>project</code>). If you need a
        collection, such as a list of departments where each department is its
        own instance, you create two resource types: one static type for the
        container and one dynamic type for the items.
      </p>
      <p className="bf-p" style={{ margin: "0 0 12px" }}>
        Creating a type here adds it to the shared catalog. You'll place it into
        your platform in the next step.
      </p>

      {onCreateType && (
        <Button onClick={() => setDrawerOpen(true)} style={{ marginTop: 8 }}>
          <Icon icon={faPlus} marginRight />
          Create resource type
        </Button>
      )}

      {createdTypes.length > 0 && (
        <p
          className="bf-p"
          style={{ margin: "12px 0 0", color: "var(--bfc-base-c-2)" }}
        >
          {createdTypes.length === 1
            ? "1 resource type created. "
            : `${createdTypes.length} resource types created. `}
          You can create more or continue to the next step.
        </p>
      )}

      <Drawer
        header="Create resource type"
        isOpen={drawerOpen}
        onRequestClose={() => setDrawerOpen(false)}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            height: "100%",
          }}
        >
          <CreateResourceTypeForm busy={busy} onCreateType={onCreateType} />

          {createdTypes.length > 0 && (
            <div>
              <span className="bf-label">Created resource types</span>
              <ul
                style={{
                  listStyle: "none",
                  margin: "4px 0 0",
                  padding: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                {createdTypes.map((t) => (
                  <li
                    key={t.id}
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Icon
                      icon={faCheck}
                      style={{ color: "var(--bfc-chill, #2e7d32)" }}
                    />
                    <span>
                      {t.displayName} <code className="bf-small">{t.slug}</code>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button
            variant="filled"
            onClick={() => setDrawerOpen(false)}
            style={{ marginTop: "auto" }}
          >
            Finish
          </Button>
        </div>
      </Drawer>
    </>
  );
}

function CreateResourceTypeForm({
  busy,
  onCreateType,
}: {
  busy?: boolean;
  onCreateType?: (
    req: CreateResourceTypeRequest,
  ) => Promise<ResourceType | undefined>;
}) {
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [cardinality, setCardinality] = useState<"single" | "many">("single");

  const [literalPrefix, setLiteralPrefix] = useState("");
  const [placeholder, setPlaceholder] = useState("");
  const [idPattern, setIdPattern] = useState<keyof typeof ID_PATTERNS>("guid");
  const [customPattern, setCustomPattern] = useState("");

  const builderPattern =
    idPattern === "custom"
      ? customPattern.trim()
      : ID_PATTERNS[idPattern].pattern;

  const generatedRegex =
    cardinality === "single"
      ? `^${slug.trim()}$`
      : placeholder.trim()
        ? composeDynamicRegex(
            literalPrefix.trim(),
            placeholder.trim(),
            builderPattern,
          )
        : null;

  const previewSegment = generatedRegex
    ? deriveSegmentTemplate(generatedRegex)
    : null;

  const canCreate =
    !busy &&
    slug.trim() !== "" &&
    displayName.trim() !== "" &&
    onCreateType &&
    (cardinality === "single" ||
      (placeholder.trim() &&
        isValidPlaceholder(placeholder.trim()) &&
        builderPattern.length > 0));

  const handleCreate = async () => {
    if (!canCreate) return;
    const result = await onCreateType({
      slug: slug.trim(),
      displayName: displayName.trim(),
      nameRegex: generatedRegex ?? undefined,
      isStatic: cardinality === "single",
    });
    if (result) {
      setSlug("");
      setDisplayName("");
      setSlugTouched(false);
      setCardinality("single");
      setLiteralPrefix("");
      setPlaceholder("");
      setIdPattern("guid");
      setCustomPattern("");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Message state="neutral" header="Creating a type">
        Use clear, general names since resource types are shared across
        platforms. Slugs should be kebab-case (e.g. <code>server</code>,{" "}
        <code>cost-center</code>). You can create as many as you need before
        moving on.
      </Message>
      <Input
        label="Display name"
        placeholder="e.g. Server"
        value={displayName}
        onChange={(e) => {
          setDisplayName(e.target.value);
          if (!slugTouched) setSlug(slugify(e.target.value));
        }}
      />
      <Input
        label="Slug"
        placeholder="e.g. server"
        value={slug}
        onChange={(e) => {
          setSlug(e.target.value);
          setSlugTouched(true);
        }}
      />
      <fieldset
        className="bf-fieldset"
        style={{ margin: 0, padding: 0, border: "none" }}
      >
        <legend className="bf-label">Static or dynamic?</legend>
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <RadioCard
            name="learn-cardinality"
            label="Static"
            checked={cardinality === "single"}
            onChange={() => setCardinality("single")}
          >
            <span style={{ fontSize: "0.875em", color: "var(--bfc-base-c-2)" }}>
              A single fixed node. The path segment will be the slug itself.
            </span>
          </RadioCard>
          <RadioCard
            name="learn-cardinality"
            label="Dynamic"
            checked={cardinality === "many"}
            onChange={() => setCardinality("many")}
          >
            <span style={{ fontSize: "0.875em", color: "var(--bfc-base-c-2)" }}>
              Many instances, each identified by a unique value (GUID, slug,
              etc.).
            </span>
          </RadioCard>
        </div>
      </fieldset>
      {cardinality === "many" && (
        <>
          <Message state="neutral" header="Dynamic types">
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              <li>
                <strong>Variable name</strong>: the key developers use to pass
                an ID in code (e.g. <code>{"{ serverId: 'srv-123' }"}</code>).
                The SDK maps each name to the correct position in the path.
              </li>
              <li>
                <strong>ID format</strong>: all instances of this type must use
                the same format so paths stay predictable (e.g. always GUIDs,
                not a mix of GUIDs and freeform names).
              </li>
              <li>
                <strong>Path prefix</strong>: if two dynamic types share the
                same ID format, a prefix tells them apart in the path (e.g.{" "}
                <code>srv-</code> for servers and <code>comp-</code> for
                computers, both followed by a GUID).
              </li>
            </ul>
          </Message>
          <Input
            label="Variable name"
            placeholder="e.g. serverId"
            value={placeholder}
            onChange={(e) => setPlaceholder(e.target.value)}
            feedback={
              placeholder.trim() && !isValidPlaceholder(placeholder.trim())
                ? "Letters, digits and underscore; cannot start with a digit"
                : undefined
            }
            state={
              placeholder.trim() && !isValidPlaceholder(placeholder.trim())
                ? "alert"
                : undefined
            }
          />
          <div>
            <span className="bf-label">ID format</span>
            <p
              style={{
                margin: "0 0 4px",
                fontSize: "0.875em",
                color: "var(--bfc-base-c-2)",
              }}
            >
              How are individual instances identified? Most services use GUIDs.
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              {(
                Object.keys(ID_PATTERNS) as Array<keyof typeof ID_PATTERNS>
              ).map((key) => (
                <Tooltip key={key} content={ID_PATTERNS[key].description}>
                  <Button
                    small
                    variant={idPattern === key ? "filled" : "flat"}
                    onClick={() => setIdPattern(key)}
                  >
                    {ID_PATTERNS[key].label}
                  </Button>
                </Tooltip>
              ))}
            </div>
            {idPattern === "custom" && (
              <Input
                label="Custom pattern (without anchors or prefix)"
                placeholder="e.g. [a-z0-9]{6}"
                value={customPattern}
                onChange={(e) => setCustomPattern(e.target.value)}
                style={{ marginTop: 8 }}
                required
              />
            )}
          </div>
          <Input
            label="Path prefix"
            description="A prefix that distinguishes this type from siblings sharing the same ID format (e.g. srv- vs comp-)."
            placeholder="e.g. srv-"
            value={literalPrefix}
            onChange={(e) => setLiteralPrefix(e.target.value)}
          />
        </>
      )}

      {cardinality === "many" && generatedRegex && (
        <p
          style={{
            margin: 0,
            fontSize: "0.875em",
            color: "var(--bfc-base-c-2)",
          }}
        >
          Path segment: <code>{previewSegment ?? generatedRegex}</code>
        </p>
      )}

      <Button
        variant="filled"
        state={canCreate ? undefined : "inactive"}
        onClick={handleCreate}
      >
        Create
      </Button>
    </div>
  );
}

function AddFirstResourceContent() {
  return (
    <>
      <p className="bf-p" style={{ margin: "0 0 12px" }}>
        In the previous step you defined a reusable type. Now you'll place it
        into your platform, which creates an actual resource in your structure,
        separate from the shared type.
      </p>
      <p className="bf-p" style={{ margin: "0 0 12px" }}>
        The same type can be placed in many spots. A <code>settings</code> type,
        for instance, could sit under a server and under a computer. Each
        placement is its own resource with its own qualified name.
      </p>
      <p className="bf-p" style={{ margin: 0 }}>
        Click the <strong>pulsing + button</strong> on the platform node on the
        canvas below to open the drawer, then pick the type you created, or
        choose a pre-existing one, and give it a qualified name. This is exactly
        how you'll add resources in the build phase.
      </p>
    </>
  );
}

/**
 * Simplified add-resource form shown in the drawer during the "Add your first
 * resource" learn step. Unlike the full build-phase drawer it only picks an
 * existing type from the catalog (the one created in the previous step) and
 * sets a qualified name — no create-type, linked platforms, or delete affordances.
 */
export function LearnFirstResourceDrawer({
  selection,
  availableTypes,
  busy,
  onAddUsage,
  onAdded,
  createdTypes,
}: {
  selection: BlueprintSelection | null;
  availableTypes: ResourceType[];
  busy?: boolean;
  onAddUsage: (
    platformSlug: string,
    parentUsageId: string | null,
    typeId: string,
    qualifiedName: string,
  ) => Promise<void> | void;
  onAdded: () => void;
  /** Types created in the previous step, offered as quick-pick buttons. */
  createdTypes: ResourceType[];
}) {
  const [typeId, setTypeId] = useState("");
  const [qualifiedName, setQualifiedName] = useState("");
  const [qnTouched, setQnTouched] = useState(false);

  // Quick-pick buttons for the types created in the previous step, limited to
  // those still addable here (not already used as a sibling).
  const quickPickTypes = createdTypes.filter((t) =>
    availableTypes.some((a) => a.id === t.id),
  );

  const selectType = (id: string) => {
    setTypeId(id);
    setQnTouched(false);
  };

  const typeOptions = availableTypes.map((t) => ({
    value: t.id,
    label: `${t.displayName} (${t.slug})`,
  }));

  const selectedType = typeId
    ? (availableTypes.find((t) => t.id === typeId) ?? null)
    : null;
  const selectedOption = typeId
    ? (typeOptions.find((o) => o.value === typeId) ?? null)
    : null;

  // Suggest a qualified name from the type's slug (singularized for dynamic
  // types) until the user edits it themselves.
  useEffect(() => {
    if (qnTouched) return;
    if (!selectedType) {
      setQualifiedName("");
      return;
    }
    const slug = selectedType.slug;
    const segment =
      !selectedType.isStatic && slug.endsWith("s") && slug.length > 1
        ? slug.slice(0, -1)
        : slug;
    setQualifiedName(segment);
  }, [selectedType, qnTouched]);

  if (!selection || selection.kind !== "platform") {
    return (
      <p
        className="bf-p bf-small"
        style={{ color: "var(--bfc-base-c-2)", margin: 0 }}
      >
        Click the + button on the platform node to add your first resource.
      </p>
    );
  }

  const canAdd = !busy && typeId && qualifiedName.trim() !== "";

  const handleAdd = async () => {
    if (!canAdd) return;
    try {
      await onAddUsage(
        selection.platformSlug,
        null,
        typeId,
        qualifiedName.trim(),
      );
    } catch {
      // The host already surfaced the error in its banner; keep the drawer
      // open so the entered type and name are not lost.
      return;
    }
    onAdded();
  };

  return (
    <div className="bp-drawer-body" style={{ marginTop: 16 }}>
      {availableTypes.length > 0 ? (
        <Grid gap={12}>
          {quickPickTypes.length > 0 && (
            <div>
              <span className="bf-label">The type you created</span>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 4,
                }}
              >
                {quickPickTypes.map((t) => (
                  <Button
                    key={t.id}
                    small
                    variant={typeId === t.id ? "filled" : "flat"}
                    onClick={() => selectType(t.id)}
                  >
                    {t.displayName}
                  </Button>
                ))}
              </div>
            </div>
          )}
          <Select
            label={
              quickPickTypes.length > 0
                ? "Or search all types"
                : "Resource type"
            }
            placeholder="Search for a type…"
            options={typeOptions}
            value={selectedOption}
            onChange={(opt) => {
              setTypeId((opt as { value: string } | null)?.value ?? "");
              setQnTouched(false);
            }}
            isClearable
          />
          <Message state="neutral" header="What is a qualified name?">
            Used in the SDKs as the key developers pass when coding (e.g.{" "}
            <code>[RequireAccess("server")]</code>), instead of the full path{" "}
            <code>resource:nimpos/workload-manager/server</code>. If the same
            type sits in many places, nest the name to keep each one unique:{" "}
            <code>server.settings</code>, <code>computer.settings</code>.
          </Message>
          <Input
            label="Qualified name"
            placeholder="e.g. servers"
            value={qualifiedName}
            onChange={(e) => {
              setQualifiedName(e.target.value);
              setQnTouched(true);
            }}
          />
          <Button
            variant="filled"
            state={canAdd ? undefined : "inactive"}
            onClick={handleAdd}
          >
            Add to platform
          </Button>
        </Grid>
      ) : (
        <p className="bf-p" style={{ color: "var(--bfc-base-c-2)" }}>
          No resource types available yet. Go back to “Create a resource type”
          to create one first.
        </p>
      )}
    </div>
  );
}

function DoneContent() {
  return (
    <>
      <p className="bf-p" style={{ margin: "0 0 8px" }}>
        You've added your first node to the blueprint! In the build phase you
        can continue adding resources, create new types, and build out the full
        structure of your platform.
      </p>
      <p className="bf-p" style={{ margin: 0 }}>
        Click <strong>Start building</strong> below to continue.
      </p>
    </>
  );
}
